import { useRef, useEffect, useState } from "react";
import { Coffee, Tv, Utensils, Beer } from "lucide-react";

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => { const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.15 }); if (ref.current) obs.observe(ref.current); return () => obs.disconnect(); }, [ref]);
  return inView;
}

const snackbarHighlights = [
  { icon: Utensils, text: "Snacks & refeições ligeiras" },
  { icon: Beer, text: "Bebidas frescas e café" },
  { icon: Tv, text: "Ecrãs com jogos em direto" },
  { icon: Coffee, text: "Vista para os campos" },
];

const snackbarImages = [
  "/assets/img/galeria-1.webp",
  "/assets/img/comida.webp",
  "/assets/img/galeria-2.webp",
];

export default function PostGame() {
  const ref = useRef(null);
  const inView = useInView(ref);
  const [currentImageIdx, setCurrentImageIdx] = useState(0);

  // Auto-rotate carousel every 2 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIdx((prev) => (prev + 1) % snackbarImages.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="postgame" data-testid="postgame-section" ref={ref} className="py-24 bg-[#0a0f1a]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className={`${inView ? 'fade-in-up' : 'opacity-0'}`}>
          
          {/* Mobile Layout */}
          <div className="lg:hidden">
            <div className="bg-[#0d1320] rounded-2xl p-6 border border-white/5">
              <span className="font-body text-xs font-medium uppercase tracking-widest text-jp-lime mb-3 block">Pós-Jogo</span>
              <h3 className="font-display text-2xl text-white font-medium mb-4">Depois da batalha, a recompensa...</h3>
              
              {/* Carousel Mobile */}
              <div className="relative rounded-xl overflow-hidden mb-6 aspect-square">
                {snackbarImages.map((img, i) => (
                  <img
                    key={i}
                    src={img}
                    alt={`Snack-bar ${i + 1}`}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${i === currentImageIdx ? 'opacity-100' : 'opacity-0'}`}
                    loading="lazy"
                  />
                ))}
                {/* Dots indicator */}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                  {snackbarImages.map((_, i) => (
                    <div
                      key={i}
                      className={`w-2 h-2 rounded-full transition-[background-color,transform] ${i === currentImageIdx ? 'bg-jp-lime scale-150' : 'bg-white/30'}`}
                    />
                  ))}
                </div>
              </div>

              <p className="font-body text-white/35 text-sm leading-relaxed mb-4">
                Relaxe no nosso snack-bar com vista para os campos.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {snackbarHighlights.map((item, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`snackbar-highlight-mobile-${i}`}>
                    <item.icon className="w-4 h-4 text-jp-lime" strokeWidth={1.5} />
                    <span className="font-body text-white/40 text-xs">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Desktop Layout: Carousel left, Text right */}
          <div className="hidden lg:block">
            <div className="bg-[#0d1320] rounded-2xl p-8 border border-white/5">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                {/* Carousel on the left */}
                <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
                  {snackbarImages.map((img, i) => (
                    <img
                      key={i}
                      src={img}
                      alt={`Snack-bar ${i + 1}`}
                      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${i === currentImageIdx ? 'opacity-100' : 'opacity-0'}`}
                    />
                  ))}
                  {/* Dots indicator */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {snackbarImages.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentImageIdx(i)}
                        aria-label={`Ver imagem ${i + 1}`}
                        className={`w-2 h-2 rounded-full transition-[background-color,transform] ${i === currentImageIdx ? 'bg-jp-lime scale-[1.75]' : 'bg-white/30 hover:bg-white/50'}`}
                        data-testid={`postgame-carousel-dot-${i}`}
                      />
                    ))}
                  </div>
                </div>

                {/* Text on the right */}
                <div>
                  <span className="font-body text-xs font-medium uppercase tracking-widest text-jp-lime mb-4 block">Pós-Jogo</span>
                  <h3 className="font-display text-3xl text-white font-medium mb-4">Depois da batalha, a recompensa...</h3>
                  <p className="font-body text-white/35 text-base leading-relaxed mb-6">
                    Relaxe no nosso snack-bar com vista para os campos. Snacks, bebidas e refeições ligeiras num ambiente acolhedor.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    {snackbarHighlights.map((item, i) => (
                      <div key={i} className="flex items-center gap-3 bg-[#0a0f1a] rounded-xl p-4" data-testid={`snackbar-highlight-${i}`}>
                        <item.icon className="w-5 h-5 text-jp-lime" strokeWidth={1.5} />
                        <span className="font-body text-white/50 text-base">{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
